import { Component, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { StorageKeys, SessionStorageKeys, StoreService } from '../../services/store.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { GridItemResizedMessage, ToggleWellSchematicSidebarMessage } from 'src/app/shared/models/mediator-messages.model';
import { WellSchematicComponent } from 'src/app/wellbore-inputs/components/well-schematic/well-schematic.component';
import { UsersService } from '../../services/users-service';
import { lastValueFrom, Subject, Subscription, takeUntil } from 'rxjs';
import { CurrentAppIds, User } from '../user-admin-page/user-model';
import { CurrentAppIdsDto } from '../../models/current-app-ids-dto';
import { ActivatedRoute, Router } from '@angular/router';
import { DesignContextService } from '../../services/design-context.service';

@Component({
    selector: 'app-well-designer-page',
    templateUrl: './well-designer-page.component.html',
    styleUrls: ['./well-designer-page.component.scss'],
    standalone: false
})
export class WellDesignerPageComponent implements OnInit, OnDestroy {

  public appIds = signal<CurrentAppIds | null>(null);
  public showWellSchematic = signal(false);
  public user = signal<User | null>(null);
  private _subscriptions = new Subscription();
  private _destroy$ = new Subject<void>();
  private _routeDesignBootstrapDone = false;
  
  @ViewChild('schematic')
  public wellSchematic!: WellSchematicComponent;

  constructor(
    private _mediator: MediatorService,
    private _storeService: StoreService,
    private _usersService: UsersService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _designContextService: DesignContextService
  ) {}

  ngOnInit(): void {

    // Subscribe to store changes
    this._storeService.watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS)
      .pipe(takeUntil(this._destroy$))
      .subscribe(appIds => {
        this.appIds.set(appIds);
        void this.syncDesignContext(appIds);
    });

    // Subscribe to mediator messages
    this._subscriptions.add(
      this._mediator.of(ToggleWellSchematicSidebarMessage).subscribe(() => {
        this.toggleWellSchematic();
      })
    );

    // Fetch user and save user-related data to the store.
    // Keep this out of async ngOnInit timing to avoid NG0100 during first render.
    this.loadUser();
  }

  private async loadUser(): Promise<void> {
    const user = await lastValueFrom(this._usersService.getUser());
    this.user.set(user);

    await this._storeService.set(StorageKeys.UNITS, user?.units);
    await this._storeService.set(StorageKeys.ROLES, user?.roles);
    await this._storeService.set(StorageKeys.USER, user);
  }

  private async syncDesignContext(appIds: CurrentAppIdsDto | null): Promise<void> {
    const routeDesignId = this._route.snapshot.paramMap.get('designId');
    const isFirstSync = !this._routeDesignBootstrapDone;

    await this._designContextService.syncDesignContext(appIds, routeDesignId, {
      isFirstSync,
      onNavigate: (designId: string) => {
        void this._router.navigate(['/designs', designId], { replaceUrl: true });
      }
    });

    this._routeDesignBootstrapDone = true;
  }

  public requestSchematicRedraw() {
    this.wellSchematic.drawSchematic();
    // Fire event to resize components.
    this._mediator.publish(new GridItemResizedMessage("", 0, 0));
  }

  private toggleWellSchematic() {
    this.showWellSchematic.update((isOpen) => !isOpen);
    this.publishGridResizeAfterLayout();
  }

  private publishGridResizeAfterLayout(): void {
    // Toggling the schematic changes the page grid columns. Publish resize after
    // Angular renders the new layout so widgets recompute against final dimensions.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._mediator.publish(new GridItemResizedMessage('', 0, 0));
      });
    });
  }

  ngOnDestroy(): void {
    this._subscriptions?.unsubscribe();
    this._destroy$.next(); // Emit a value to trigger unsubscription
    this._destroy$.complete(); // Complete the subject to clean up
  }
}
